<template>
    <div class="addon-item">
        <Addon
            v-model="selected"
            :price="item.Price"
            :illustration="illustration"
            :title="title"
            :rows="rows"
            :field-error="hasErrors"
            :errors="errors"
            :showSolidLogo="showSolidLogo">
            <template #readmorelinks>
                <Link
                    v-if="item.LinkUrlSecondary"
                    :href="item.LinkUrlSecondary"
                    :label="t({ key: 'shared.details' })"
                    target="_blank"
                    class="link" />
                <Link
                    v-if="item.LinkUrl"
                    :href="item.LinkUrl"
                    :label="t({ key: 'shared.read_more' })"
                    target="_blank"
                    class="link" />
            </template>
        </Addon>

        <WarningModal
            v-model="selected"
            :visible="showWarning"
            :onSelect="(value) => (value ? (selected = true) : setShowWarning(false))">
            <AddonWarning :addon="item" />
        </WarningModal>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import { useStore } from '@/store';
    import { logAnalyticsAddRemoveProduct } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { EventName } from '@/tracking/analytics/types';
    import { logStatsigCheckoutAddRemoveAddon } from '@/tracking/statsig/checkout.functions';
    import { isEmpty } from 'lodash';
    import { StatsigEvent } from '@/tracking/statsig/types';
    import { AddonProduct, SerializationType } from '@/api/Cart/cart.interfaces';
    import { getAddonIllustration, getAddonTexts, getAddonTitle } from '@/helpers/addon';
    import { addonsToRedirect } from '@/helpers/termsAndPoliciesLinks';
    import Addon from '@ui/components/Addon/Addon.vue';
    import WarningModal from '@/components/shared/WarningModal.vue';
    import AddonWarning from '@/components/shared/AddonWarning/AddonWarning.vue';
    import Link from '@ui/components/Link/Link.vue';

    export default defineComponent({
        name: 'AddonItem',
        methods: { t },
        components: {
            Link,
            Addon,
            WarningModal,
            AddonWarning,
        },
        emits: ['update:modelValue'],
        props: {
            modelValue: {
                type: Boolean as PropType<boolean | undefined>,
                default: undefined,
            },
            item: { type: Object as PropType<AddonProduct>, required: true },
            errors: { type: Array as PropType<string[]>, default: () => [] },
        },
        setup(props, { emit }) {
            const store = useStore();
            const selected = ref<boolean | undefined>(props.modelValue);
            const showWarning = ref<boolean>(false);

            const illustration = computed(() => getAddonIllustration(props.item.$type));
            const title = computed(() => getAddonTitle(props.item.$type));
            const rows = computed(() => getAddonTexts(props.item.$type));

            const hasErrors = computed(() => !isEmpty(props.errors));
            const isTravelInsurance = computed(
                () => props.item.$type === SerializationType.TravelInsurance
            );
            const isLuggageInsurance = computed(
                () => props.item.$type === SerializationType.LuggageInsurance
            );
            const showLinks = computed(() => addonsToRedirect.includes(props.item.$type));

            watch(
                () => selected.value,
                (newValue, prevValue) => {
                    if (newValue !== prevValue) {
                        newValue ? onSelect() : onDeselect();
                    }
                }
            );

            function onSelect() {
                selected.value = true;
                setShowWarning(false);

                store.commit('cart/buyAddon', props.item);
                onSelectTracking();
                onUpdateValue();
            }

            function onDeselect() {
                selected.value = false;

                props.item.SpecialRequest = null;
                props.item.ShowDiscardPopUp ? setShowWarning(true) : setShowWarning(false);

                store.commit('cart/removeAddon', props.item);
                onDeselectTracking();
                onUpdateValue();
            }

            function onUpdateValue() {
                emit('update:modelValue', selected.value);
            }

            function onSelectTracking() {
                logAnalyticsAddRemoveProduct(props.item, EventName.CHECKOUT_ADD_PRODUCT);
                logStatsigCheckoutAddRemoveAddon(props.item, StatsigEvent.CHECKOUT_ADD_ADDON);
            }

            function onDeselectTracking() {
                logAnalyticsAddRemoveProduct(props.item, EventName.CHECKOUT_REMOVE_PRODUCT);
                logStatsigCheckoutAddRemoveAddon(
                    props.item,
                    StatsigEvent.CHECKOUT_REMOVE_ADDON
                );
            }

            function setShowWarning(visible: boolean) {
                showWarning.value = visible;
            }

            const showSolidLogo = computed(
                () => isLuggageInsurance.value || isTravelInsurance.value
            );

            return {
                illustration,
                title,
                rows,
                hasErrors,
                showWarning,
                setShowWarning,
                onSelect,
                selected,
                showLinks,
                showSolidLogo,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './AddonItem.scss';
</style>
